import type { IWebGlRender, Nullable, Property, ScaleType, VertexAttribute } from '@library/types'
import {
  bindAttribute,
  bindIndexBuffer,
  createAttribute,
  getOrCreateProgram,
  getProjectionMatrix,
  getWebGlContext,
  unbindAttribute,
} from '@library/utils'
import type { ColorType } from 'common/helpers'

import { colorTypeToNormFloat32Array } from '../../helpers'
import { updateBuffers } from './helpers'
import { fragmentShaderSource, vertexShaderSource } from './shaders'

type Attributes = 'aPosition'

export class FilledTrianglesRender implements IWebGlRender, Disposable {
  private program: WebGLProgram
  private renderingContext: WebGL2RenderingContext

  private attributes: Record<Attributes, VertexAttribute>

  private indexBuffer: Nullable<WebGLBuffer>

  private globalProjectionUniform: Nullable<WebGLUniformLocation>
  private colorUniform: Nullable<WebGLUniformLocation>

  private indexesCount: number

  private isDisposed: boolean
  private color: Float32Array

  constructor(
    private canvas: HTMLCanvasElement,
    programs: Property<Record<string, WebGLProgram>>,
    private zoom: ScaleType,
    private points: Float32Array,
    private indexes: Uint32Array,
    private lineWidth: number,
    color: ColorType,
  ) {
    const gl = getWebGlContext(canvas)
    this.renderingContext = gl

    const program = getOrCreateProgram(programs, gl, vertexShaderSource, fragmentShaderSource)
    this.program = program

    this.attributes = {
      aPosition: createAttribute(gl, program, 'aPosition', 3, gl.FLOAT),
    }

    this.indexBuffer = null
    this.globalProjectionUniform = gl.getUniformLocation(program, 'uGlobalProjection')
    this.colorUniform = gl.getUniformLocation(program, 'uColor')

    this.indexesCount = 0

    this.updateAttributeBuffers()

    this.isDisposed = false

    this.color = colorTypeToNormFloat32Array(color, 0.8)
  }

  public render() {
    if (this.isDisposed) return

    const renderingContext = this.renderingContext

    renderingContext.enable(renderingContext.BLEND)
    renderingContext.blendFunc(renderingContext.ONE, renderingContext.ONE_MINUS_SRC_ALPHA)

    renderingContext.useProgram(this.program)

    for (const [key, value] of Object.entries(this.attributes)) {
      bindAttribute(renderingContext, value)
    }

    bindIndexBuffer(renderingContext, this.indexBuffer)

    renderingContext.uniformMatrix3fv(
      this.globalProjectionUniform,
      false,
      getProjectionMatrix(this.canvas, this.zoom),
    )

    renderingContext.uniform4fv(this.colorUniform, this.color)

    renderingContext.drawElements(
      renderingContext.TRIANGLES,
      this.indexesCount,
      renderingContext.UNSIGNED_INT,
      0,
    )

    for (const [key, value] of Object.entries(this.attributes)) {
      unbindAttribute(renderingContext, value)
    }
  }

  public setZoomState(zoom: ScaleType) {
    this.zoom = zoom
  }

  public setLineWidth(lineWidth: number) {
    this.lineWidth = lineWidth
  }

  public setPoints(points: Float32Array) {
    this.points = points
    this.updateAttributeBuffers()
  }

  private updateAttributeBuffers() {
    const { indexBuffer, indexesCount } = updateBuffers(
      this.renderingContext,
      this.attributes.aPosition,
      this.points,
      this.indexes,
    )
    this.indexBuffer = indexBuffer
    this.indexesCount = indexesCount
  }

  [Symbol.dispose](): void {
    const renderingContext = this.renderingContext
    for (const [key, value] of Object.entries(this.attributes)) {
      renderingContext.deleteBuffer(value.buffer)
    }
    renderingContext.deleteBuffer(this.indexBuffer)
    this.isDisposed = true
  }
}
